import { create } from 'zustand';
import textbookService from '../services/textbookService';


// 创建AI短文听写页面的状态管理
const useAiDictationStore = create((set, get) => ({
  // 选择的单词来源类型: 'manual', 'textbook', 'random'
  sourceType: null,
  
  // 已选单词列表
  selectedWords: [],
  
  // 生成的短文
  generatedStory: {
    text: "",
    isLoading: false,
  },
  
  // 听写设置
  dictationSettings: {
    readingSpeed: 'normal', // 'slow', 'normal', 'fast'
    repeatCount: 2,
    sentenceInterval: 5,
  },
  
  // 模态框状态
  modal: {
    isOpen: false,
    type: null, // 'manual', 'textbook', 'random'
  },
  
  // 设置单词来源类型
  setSourceType: (type) => set({ sourceType: type }),
  
  // 添加单词
  addWord: (word) => set((state) => ({
    selectedWords: [...state.selectedWords, { 
      id: typeof word.id !== 'undefined' ? word.id : Date.now(), 
      word: typeof word === 'string' ? word : word.word,
      // 如果传入的是单词对象，保留其他属性
      ...(typeof word !== 'string' && word)
    }]
  })),
  
  // 移除单词
  removeWord: (wordId) => set((state) => ({
    selectedWords: state.selectedWords.filter(w => w.id !== wordId)
  })),
  
  // 清空所有单词
  clearWords: () => set({ selectedWords: [] }),
  
  // 设置单词列表
  setWords: (words) => set({ 
    selectedWords: words.map(word => ({
      id: typeof word.id !== 'undefined' ? word.id : Date.now(),
      word: typeof word === 'string' ? word : word.word,
      // 如果传入的是单词对象，保留其他属性
      ...(typeof word !== 'string' && word)
    }))
  }),
  
  // 从课本中随机选择单词
  selectRandomWords: (count = 7, gradeFilter = null, termFilter = null, difficultyFilter = null) => {
    const randomWords = textbookService.getRandomWords(count, {
      grade: gradeFilter,
      term: termFilter,
      difficulty: difficultyFilter
    });
    
    // 设置选中的单词
    set({ selectedWords: randomWords });
  },
  
  // 重新生成短文
  regenerateStory: () => {
    set(state => ({ 
      generatedStory: { 
        ...state.generatedStory, 
        isLoading: true 
      } 
    }));
    
    // 模拟API请求延迟
    setTimeout(() => {
      const words = get().selectedWords.map(w => w.word);
      const newStory = generateStoryWithWords(words);
      
      set({ 
        generatedStory: { 
          text: newStory, 
          isLoading: false 
        } 
      });
    }, 1500);
  },
  
  // 听写设置相关方法
  increaseReadingSpeed: () => set((state) => {
    const speeds = ['slow', 'normal', 'fast'];
    const currentIndex = speeds.indexOf(state.dictationSettings.readingSpeed);
    const newIndex = Math.min(currentIndex + 1, speeds.length - 1);
    
    return {
      dictationSettings: {
        ...state.dictationSettings,
        readingSpeed: speeds[newIndex]
      }
    };
  }),
  
  decreaseReadingSpeed: () => set((state) => {
    const speeds = ['slow', 'normal', 'fast'];
    const currentIndex = speeds.indexOf(state.dictationSettings.readingSpeed);
    const newIndex = Math.max(currentIndex - 1, 0);
    
    return {
      dictationSettings: {
        ...state.dictationSettings,
        readingSpeed: speeds[newIndex]
      }
    };
  }),
  
  increaseRepeatCount: () => set((state) => ({
    dictationSettings: {
      ...state.dictationSettings,
      repeatCount: Math.min(state.dictationSettings.repeatCount + 1, 5)
    }
  })),
  
  decreaseRepeatCount: () => set((state) => ({
    dictationSettings: {
      ...state.dictationSettings,
      repeatCount: Math.max(state.dictationSettings.repeatCount - 1, 1)
    }
  })),
  
  increaseSentenceInterval: () => set((state) => ({
    dictationSettings: {
      ...state.dictationSettings,
      sentenceInterval: Math.min(state.dictationSettings.sentenceInterval + 1, 10)
    }
  })),
  
  decreaseSentenceInterval: () => set((state) => ({
    dictationSettings: {
      ...state.dictationSettings,
      sentenceInterval: Math.max(state.dictationSettings.sentenceInterval - 1, 2)
    }
  })),
  
  // 打开模态框
  openModal: (type) => set({ 
    modal: { 
      isOpen: true, 
      type 
    } 
  }),
  
  // 关闭模态框
  closeModal: () => set({ 
    modal: { 
      isOpen: false, 
      type: null 
    } 
  }),
}));

// 辅助函数：根据单词生成短文
function generateStoryWithWords(words) {
  // 如果没有单词，返回空字符串
  if (!words || words.length === 0) return "";
  
  // 这里是一个简单的模板，实际应用中可能需要调用AI API
  const templates = [
    "Sarah loves to eat <span class=\"word-highlight\">{0}</span> from her garden. Every morning, she peels a ripe <span class=\"word-highlight\">{1}</span> for breakfast. Her friend Tom grows <span class=\"word-highlight\">{2}</span> in his backyard. They often share a juicy <span class=\"word-highlight\">{3}</span> after school. In autumn, they pick <span class=\"word-highlight\">{4}</span> from the old tree. During summer, they enjoy sweet <span class=\"word-highlight\">{5}</span> slices. Sometimes they add <span class=\"word-highlight\">{6}</span> flakes to their fruit salad.",
    "John went to the market to buy some <span class=\"word-highlight\">{0}</span> for his mother. On the way, he saw a monkey eating a <span class=\"word-highlight\">{1}</span>. The monkey was sitting under a vine full of <span class=\"word-highlight\">{2}</span>. John offered the monkey a <span class=\"word-highlight\">{3}</span> from his bag. The monkey preferred a <span class=\"word-highlight\">{4}</span> instead. Later, they shared a <span class=\"word-highlight\">{5}</span> under the shade. The monkey showed John how to crack open a <span class=\"word-highlight\">{6}</span>."
  ];
  
  // 随机选择一个模板
  const template = templates[Math.floor(Math.random() * templates.length)];
  
  // 将单词填入模板
  let story = template;
  words.forEach((word, index) => {
    // 如果模板中有对应的占位符，则替换
    if (story.includes(`{${index}}`)) {
      story = story.replace(`{${index}}`, word);
    }
  });
  
  return story;
}

export default useAiDictationStore;